'use client'

import { useTheme } from 'next-themes'
import type { RefObject } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLiveHeatmapColumn } from '@/features/chart/hooks/use-live-heatmap-column'
import {
  autoHeatmapGrouping,
  barTimeToX,
  bucketAt,
  buildHeatmapRamp,
  buildIntensityScale,
  calibrateBarCenter,
  clipHeatmapColumn,
  filterHeatmapColumnBySize,
  findColumnIndex,
  groupHeatmapColumn,
  type HeatmapColumn,
  type HeatmapSide,
  type HeatmapSizeFilter,
  ORDERBOOK_HEATMAP_GROUPING_AUTO,
  passesHeatmapSizeFilter,
  resolveChartTimeZone,
  toHeatmapColumns,
  withLiveColumn,
} from '@/features/chart/lib/orderbook-heatmap'
import { useNumberFormat } from '@/hooks/use-number-format'
import { nextChartBarStartMs } from '@/lib/chart-timeframe'
import type { TVCrosshairData, TVWidgetInstance } from '@/lib/tradingview/types/tv-widget.types'
import { cn } from '@/lib/utils'
import { ChartTimeframe, type Exchange } from '@/types/generated/common/types'
import type { OrderbookHeatmap } from '@/types/generated/public_api'

const COLOR_STEPS = 24
/** Step of a cell not drawn — under the scale's floor or hidden by the size filter. The hover
 *  card still lists it. */
const HIDDEN_STEP = 255
/** Cells at least this many device px both ways are drawn as ellipses inset by a gutter, so the
 *  heatmap reads as separate tiles (Percept's look) instead of solid lines; thinner ones stay
 *  rects, since an ellipse that small is just a blurred dot. */
const ELLIPSE_MIN_PX = 4
/** An ellipse is at most this many times taller than wide, so it scales with the bar spacing:
 *  zoomed out (narrow bars) the heatmap reads as dots, not tall pills spanning grouped rows. */
const MAX_ELLIPSE_ASPECT = 1.6
const HOVER_ROWS_EACH_SIDE = 3
const HOVER_CARD_OFFSET_PX = 16
const HOVER_CARD_WIDTH_PX = 248

type PaneRect = { left: number; top: number; width: number; height: number }

type Viewport = {
  pane: PaneRect
  priceFrom: number
  priceTo: number
  barSpacing: number
  reference: { timeMs: number; centerX: number }
}

/** Each cell's colour step, parallel to its column's `bids`/`asks`. */
type CellSteps = Record<HeatmapSide, Uint8Array>

type Hover = { timeMs: number; price: number; x: number; y: number; timeZone?: string }

/**
 * The main pane's rect in the container's coordinates. `.chart-gui-wrapper` is the first pane's
 * drawing area (excluding both price axes), which is what the time scale's x=0 and the price
 * scale's visible range both refer to.
 */
function getMainPaneRect(container: HTMLElement | null): PaneRect | null {
  const iframe = container?.querySelector('iframe')
  const pane = iframe?.contentDocument?.querySelector<HTMLElement>('.chart-gui-wrapper')
  if (!container || !iframe || !pane) return null
  const containerRect = container.getBoundingClientRect()
  const iframeRect = iframe.getBoundingClientRect()
  const paneRect = pane.getBoundingClientRect()
  if (paneRect.width <= 0 || paneRect.height <= 0) return null
  return {
    left: iframeRect.left - containerRect.left + paneRect.left,
    top: iframeRect.top - containerRect.top + paneRect.top,
    width: paneRect.width,
    height: paneRect.height,
  }
}

function readViewport(widget: TVWidgetInstance, container: HTMLElement | null): Viewport | null {
  try {
    const chart = widget.activeChart()
    const pane = getMainPaneRect(container)
    const range = chart.getPanes()[0]?.getMainSourcePriceScale()?.getVisiblePriceRange()
    if (!pane || !range || !(range.to > range.from)) return null
    const timeScale = chart.getTimeScale()
    const barSpacing = timeScale.barSpacing()
    const reference = calibrateBarCenter(
      (x) => timeScale.coordinateToTime(x),
      pane.width / 2,
      barSpacing
    )
    if (!reference) return null
    return { pane, priceFrom: range.from, priceTo: range.to, barSpacing, reference }
  } catch {
    return null
  }
}

function viewportKey(viewport: Viewport | null) {
  if (!viewport) return ''
  const { pane, priceFrom, priceTo, barSpacing, reference } = viewport
  return [
    pane.left,
    pane.top,
    pane.width,
    pane.height,
    priceFrom,
    priceTo,
    barSpacing,
    reference.timeMs,
    reference.centerX.toFixed(2),
    window.devicePixelRatio,
  ].join(':')
}

function draw(
  canvas: HTMLCanvasElement,
  viewport: Viewport,
  columns: readonly HeatmapColumn[],
  timeframe: number,
  steps: readonly CellSteps[],
  ramp: readonly string[]
) {
  const { pane, priceFrom, priceTo, barSpacing, reference } = viewport
  const dpr = window.devicePixelRatio || 1
  const widthPx = Math.round(pane.width * dpr)
  const heightPx = Math.round(pane.height * dpr)
  if (canvas.width !== widthPx) canvas.width = widthPx
  if (canvas.height !== heightPx) canvas.height = heightPx
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, widthPx, heightPx)

  const toX = barTimeToX(reference.timeMs, reference.centerX, barSpacing, timeframe)
  const pxPerPrice = heightPx / (priceTo - priceFrom)
  const toY = (price: number) => (priceTo - price) * pxPerPrice

  // One rect list and one ellipse list per colour step, shared by both sides, so fillStyle
  // changes ~48 times per frame and each ellipse list fills as one path.
  const rects = Array.from({ length: COLOR_STEPS }, () => [] as number[])
  const ellipses = Array.from({ length: COLOR_STEPS }, () => [] as number[])
  const gutter = Math.max(1, Math.round(dpr))

  const pushSide = (
    side: HeatmapSide,
    sizes: readonly number[],
    cellSteps: Uint8Array,
    start: number,
    w: number,
    x: number,
    width: number
  ) => {
    // Only the buckets overlapping the visible price range.
    const direction = side === 'bid' ? -1 : 1
    const edge = side === 'bid' ? priceTo : priceFrom
    const first = Math.max(0, Math.floor(((edge - start) * direction) / w) - 1)
    // Buckets thinner than a device pixel that round onto the same row merge into one 1px rect at
    // the strongest intensity, so a 1500-bucket column on a 400px pane paints ~400 rects.
    let runRow = -1
    let runStep = -1
    const flushRun = () => {
      if (runStep >= 0) rects[runStep].push(x, runRow, width, 1)
      runStep = -1
    }
    for (let i = first; i < sizes.length; i++) {
      const low = start + direction * i * w
      const high = low + w
      if (side === 'bid' ? high < priceFrom : low > priceTo) break
      const size = sizes[i]
      const step = cellSteps[i]
      if (!(size > 0) || step === HIDDEN_STEP) continue
      const top = Math.round(toY(high))
      const bottom = Math.round(toY(low))
      if (bottom > top) {
        flushRun()
        const height = bottom - top
        if (width >= ELLIPSE_MIN_PX && height >= ELLIPSE_MIN_PX) {
          const rx = (width - gutter) / 2
          ellipses[step].push(
            x + width / 2,
            top + height / 2,
            rx,
            Math.min((height - gutter) / 2, rx * MAX_ELLIPSE_ASPECT)
          )
        } else {
          rects[step].push(x, top, width, height)
        }
      } else if (runStep >= 0 && top === runRow) {
        runStep = Math.max(runStep, step)
      } else {
        flushRun()
        runRow = top
        runStep = step
      }
    }
    flushRun()
  }

  // Edges round the same way on both sides of a boundary, so neighbours never overlap and stack
  // alpha into a seam; columns narrower than a device pixel that round onto an x already drawn
  // are skipped for the same reason.
  let lastX = -Infinity
  columns.forEach((column, c) => {
    const x = Math.round((toX(column.startMs) - barSpacing / 2) * dpr)
    const right = Math.round(
      (toX(nextChartBarStartMs(column.startMs, timeframe)) - barSpacing / 2) * dpr
    )
    if (right < 0 || x > widthPx || x <= lastX) return
    lastX = x
    const width = Math.max(1, right - x)
    pushSide('bid', column.bids, steps[c].bid, column.bidStart, column.bucketWidth, x, width)
    pushSide('ask', column.asks, steps[c].ask, column.askStart, column.bucketWidth, x, width)
  })

  for (let step = 0; step < COLOR_STEPS; step++) {
    ctx.fillStyle = ramp[step]
    const stepRects = rects[step]
    for (let i = 0; i < stepRects.length; i += 4) {
      ctx.fillRect(stepRects[i], stepRects[i + 1], stepRects[i + 2], stepRects[i + 3])
    }
    const stepEllipses = ellipses[step]
    if (stepEllipses.length === 0) continue
    ctx.beginPath()
    for (let i = 0; i < stepEllipses.length; i += 4) {
      const cx = stepEllipses[i]
      const cy = stepEllipses[i + 1]
      const rx = stepEllipses[i + 2]
      ctx.moveTo(cx + rx, cy)
      ctx.ellipse(cx, cy, rx, stepEllipses[i + 3], 0, 0, Math.PI * 2)
    }
    ctx.fill()
  }
}

function HoverCard({
  hover,
  columns,
  timeframe,
  containerWidth,
  containerHeight,
}: {
  hover: Hover
  columns: readonly HeatmapColumn[]
  timeframe: number
  containerWidth: number
  containerHeight: number
}) {
  const format = useNumberFormat()
  const index = findColumnIndex(columns, hover.timeMs, timeframe)
  if (index < 0) return null
  const column = columns[index]
  const center = bucketAt(column, hover.price)
  if (!center) return null

  const w = column.bucketWidth
  const neighbours = [columns[index - 1], column, columns[index + 1]]
  const rows = Array.from({ length: HOVER_ROWS_EACH_SIDE * 2 + 1 }, (_, i) => {
    const low = center.low + (HOVER_ROWS_EACH_SIDE - i) * w
    return {
      low,
      cells: neighbours.map((neighbour) => (neighbour ? bucketAt(neighbour, low + w / 2) : null)),
    }
  })
  const decimals = Math.max(0, Math.min(8, -Math.floor(Math.log10(w))))
  const price = (value: number) =>
    format.number(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  const size = (value: number) =>
    format.number(value, { notation: 'compact', maximumFractionDigits: 2 })
  // Daily and weekly columns all open at the same clock time, so they're told apart by date;
  // monthly ones by month, read in UTC so a zone west of it doesn't show the last day before.
  const time = (startMs: number) =>
    new Date(startMs).toLocaleString(
      [],
      timeframe === ChartTimeframe.CHART_TIMEFRAME_MONTH_1
        ? { month: 'short', year: '2-digit', timeZone: 'UTC' }
        : timeframe >= 86_400
          ? { month: 'short', day: 'numeric', timeZone: hover.timeZone }
          : { hour: '2-digit', minute: '2-digit', timeZone: hover.timeZone }
    )

  const left =
    hover.x + HOVER_CARD_OFFSET_PX + HOVER_CARD_WIDTH_PX > containerWidth
      ? hover.x - HOVER_CARD_OFFSET_PX - HOVER_CARD_WIDTH_PX
      : hover.x + HOVER_CARD_OFFSET_PX
  // The card's height depends on the rows, so in the lower half anchor its bottom above the
  // cursor instead of measuring it.
  const vertical =
    hover.y > containerHeight / 2
      ? { bottom: containerHeight - hover.y + HOVER_CARD_OFFSET_PX }
      : { top: hover.y + HOVER_CARD_OFFSET_PX }

  return (
    <div
      className="bg-popover text-popover-foreground border-border pointer-events-none absolute z-40 rounded-md border p-2 text-[10px] shadow-md"
      style={{ left, ...vertical, width: HOVER_CARD_WIDTH_PX }}
    >
      <div className="text-muted-foreground mb-1 flex justify-between tabular-nums">
        <span>
          {new Date(column.startMs).toLocaleString([], {
            dateStyle: 'short',
            timeStyle: 'short',
            timeZone: hover.timeZone,
          })}
        </span>
        <span>Mid {price(column.midPrice)}</span>
      </div>
      <table className="w-full tabular-nums">
        <thead>
          <tr className="text-muted-foreground">
            <th className="pb-0.5 text-left font-normal">Price</th>
            {neighbours.map((neighbour, i) => (
              <th key={i} className="pb-0.5 text-right font-normal">
                {neighbour ? time(neighbour.startMs) : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ low, cells }) => (
            <tr key={low} className={cn(low === center.low && 'bg-muted')}>
              <td className="pr-2">{price(low)}</td>
              {cells.map((cell, i) => (
                <td
                  key={i}
                  className={cn(
                    'text-right',
                    !cell || cell.size === 0
                      ? 'text-muted-foreground'
                      : cell.side === 'bid'
                        ? 'text-chart-mint'
                        : 'text-chart-red',
                    i === 1 && 'font-medium'
                  )}
                >
                  {cell && cell.size > 0 ? size(cell.size) : '–'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Order-book liquidity heatmap, painted behind the (transparent) TradingView chart: one column per
 * bar, one cell per price bucket, coloured by resting quantity alone (bids and asks on one scale,
 * a muted Cyber ramp) so walls stand out wherever they sit. Buckets are grouped like the order
 * book widget's rows (`grouping`; Auto follows the zoom so rows stay tall enough to read as
 * tiles). The chart exposes no time→coordinate API, so x is
 * calibrated each frame from `coordinateToTime` against one bar and extrapolated linearly — which
 * is why monthly bars (uneven width) are not supported.
 *
 * While `live` is set the open bar is drawn from `OrderbookHeatmapStream` instead of its stored
 * opening snapshot, the way the candle stream keeps the open candle current.
 */
function OrderbookHeatmapOverlay({
  widget,
  containerRef,
  heatmap,
  sizeFilter,
  grouping,
  rangePct,
  live,
  exchange,
  symbol,
  timeframe,
}: {
  widget: TVWidgetInstance | null
  containerRef: RefObject<HTMLElement | null>
  heatmap: OrderbookHeatmap | undefined
  sizeFilter: HeatmapSizeFilter
  /** Multiple of the server's bucket, or ORDERBOOK_HEATMAP_GROUPING_AUTO. */
  grouping: number
  /** The server sends ±25% of mid; draw (and scale intensity over) only this band. */
  rangePct: number
  /** Whether the chart shows the open bar, so the live column is worth subscribing to. */
  live: boolean
  exchange: Exchange
  symbol: string
  timeframe: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { resolvedTheme } = useTheme()
  const ramp = useMemo(
    () => buildHeatmapRamp(COLOR_STEPS, resolvedTheme !== 'light'),
    [resolvedTheme]
  )
  const [pane, setPane] = useState<PaneRect | null>(null)
  const [hover, setHover] = useState<Hover | null>(null)

  const rawColumns = useMemo(
    () =>
      heatmap
        ? toHeatmapColumns(heatmap.columns).map((column) => clipHeatmapColumn(column, rangePct))
        : [],
    [heatmap, rangePct]
  )
  // Lives here, not in the chart widget, so its ~1s updates re-render only this memoized overlay.
  // Not gated on the stored columns: the stream is per instrument, so a timeframe switch keeps it.
  const liveColumn = useLiveHeatmapColumn({
    enabled: live,
    exchange,
    symbol,
    timeframe,
  })
  const isAutoGrouping = grouping === ORDERBOOK_HEATMAP_GROUPING_AUTO
  // Auto's factor, picked from the zoom each time the viewport moves; a state, so the grouped
  // columns are rebuilt only when it steps (1×→2×→5×…), not every frame.
  const [autoFactor, setAutoFactor] = useState(1)
  const factor = isAutoGrouping ? autoFactor : Math.max(1, grouping)
  const autoGroupingRef = useRef({ isAutoGrouping, bucketWidth: 0 })
  autoGroupingRef.current = {
    isAutoGrouping,
    bucketWidth: rawColumns.at(-1)?.bucketWidth ?? 0,
  }
  const group = useCallback(
    (column: HeatmapColumn) => (factor > 1 ? groupHeatmapColumn(column, factor) : column),
    [factor]
  )
  const storedColumns = useMemo(() => rawColumns.map(group), [group, rawColumns])
  const groupedLiveColumn = useMemo(
    () => liveColumn && group(clipHeatmapColumn(liveColumn, rangePct)),
    [group, liveColumn, rangePct]
  )
  // A cell's intensity depends only on the response and the size filter, so a stored cell's step
  // is resolved once here, not per frame. The scale is built from the stored levels that pass the
  // filter, so the shading spreads across the big orders being looked at, and a live update
  // doesn't reshade every other cell. Filtered-out cells aren't drawn, but `columns` stays whole
  // so the hover card still lists every level.
  const toCellSteps = useMemo(() => {
    const scale = buildIntensityScale(
      storedColumns.map((column) => filterHeatmapColumnBySize(column, sizeFilter))
    )
    const toSteps = (sizes: readonly number[]) =>
      Uint8Array.from(sizes, (size) => {
        const intensity = passesHeatmapSizeFilter(size, sizeFilter) ? scale(size) : 0
        return intensity > 0
          ? Math.min(COLOR_STEPS - 1, Math.floor(intensity * COLOR_STEPS))
          : HIDDEN_STEP
      })
    return (column: HeatmapColumn): CellSteps => ({
      bid: toSteps(column.bids),
      ask: toSteps(column.asks),
    })
  }, [storedColumns, sizeFilter])
  const storedSteps = useMemo(
    () => new Map(storedColumns.map((column) => [column, toCellSteps(column)])),
    [storedColumns, toCellSteps]
  )
  // The live column is shaded on the stored scale, so it waits for stored columns to build one.
  const columns = useMemo(
    () => (storedColumns.length > 0 ? withLiveColumn(storedColumns, groupedLiveColumn) : []),
    [storedColumns, groupedLiveColumn]
  )
  const steps = useMemo(
    () => columns.map((column) => storedSteps.get(column) ?? toCellSteps(column)),
    [columns, storedSteps, toCellSteps]
  )

  // The loop reads the frame through a ref, so a live update repaints once instead of restarting
  // it; each change is a new object, so identity tells the loop to repaint.
  const frameRef = useRef({ columns, steps, ramp })
  useEffect(() => {
    frameRef.current = { columns, steps, ramp }
  }, [columns, ramp, steps])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!widget || !canvas) return
    let lastFrame: typeof frameRef.current | null = null
    let lastKey: string | null = null
    let frameId = 0

    // Re-reads the chart every frame but only repaints when the viewport or the data moved:
    // panning and zooming fire no event that covers both axes, and the 500ms price-scale poll is
    // too slow to keep a full-pane fill glued to the candles.
    const tick = () => {
      frameId = requestAnimationFrame(tick)
      const frame = frameRef.current
      const viewport = frame.columns.length > 0 ? readViewport(widget, containerRef.current) : null
      const key = viewportKey(viewport)
      if (frame === lastFrame && key === lastKey) return
      lastFrame = frame
      lastKey = key
      const next = viewport?.pane ?? null
      setPane((current) =>
        current?.left === next?.left &&
        current?.top === next?.top &&
        current?.width === next?.width &&
        current?.height === next?.height
          ? current
          : next
      )
      if (viewport) {
        const { isAutoGrouping: auto, bucketWidth } = autoGroupingRef.current
        if (auto) {
          const pxPerPrice = viewport.pane.height / (viewport.priceTo - viewport.priceFrom)
          setAutoFactor(autoHeatmapGrouping(bucketWidth, pxPerPrice))
        }
        draw(canvas, viewport, frame.columns, timeframe, frame.steps, frame.ramp)
      }
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [containerRef, timeframe, widget])

  useEffect(() => {
    if (!widget) return
    let chart: ReturnType<TVWidgetInstance['activeChart']>
    try {
      chart = widget.activeChart()
    } catch {
      return
    }
    const subscription = chart.crossHairMoved?.()
    if (!subscription) return
    const onMove = ({ time, price, offsetX, offsetY }: TVCrosshairData) => {
      if (time === undefined || price === undefined) return
      if (offsetX === undefined || offsetY === undefined) return
      let timeZone: string | undefined
      try {
        timeZone = resolveChartTimeZone(
          chart.getTimezoneApi?.().getTimezone(),
          chart.symbolExt?.()?.timezone
        )
      } catch {}
      setHover({ timeMs: time * 1000, price, x: offsetX, y: offsetY, timeZone })
    }
    const iframeDocument = containerRef.current?.querySelector('iframe')?.contentDocument
    const onLeave = () => setHover(null)
    subscription.subscribe(null, onMove)
    iframeDocument?.addEventListener('mouseleave', onLeave)
    return () => {
      try {
        subscription.unsubscribe(null, onMove)
      } catch {}
      iframeDocument?.removeEventListener('mouseleave', onLeave)
      setHover(null)
    }
  }, [containerRef, widget])

  const insidePane =
    hover &&
    pane &&
    hover.x >= pane.left &&
    hover.x <= pane.left + pane.width &&
    hover.y >= pane.top &&
    hover.y <= pane.top + pane.height

  return (
    <>
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute z-20"
        style={
          pane
            ? { left: pane.left, top: pane.top, width: pane.width, height: pane.height }
            : { display: 'none' }
        }
      />
      {insidePane && columns.length > 0 ? (
        <HoverCard
          hover={hover}
          columns={columns}
          timeframe={timeframe}
          containerWidth={containerRef.current?.clientWidth ?? 0}
          containerHeight={containerRef.current?.clientHeight ?? 0}
        />
      ) : null}
    </>
  )
}

export default memo(OrderbookHeatmapOverlay)
